import React, { useState, useEffect, useMemo } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingState, ErrorState } from "@/components/ui/data-display";
import { getPoemById, type PoemWithHighlights } from "@/services/poemService";
import { getPoetById as getPoetByIdService } from '@/services/poetService';
import type { Database, Json } from '@/types/supabase';
import type { PoemHighlight } from '@/types/highlights';
import { highlightProcessor, ProcessedHighlightData } from '@/services/highlightProcessingService';
import HighlightedLine from "@/components/highlight/HighlightedLine";
import HighlightLegend from "@/components/highlight/HighlightLegend";
import { TooltipProvider as HighlightTooltipProvider } from '@/contexts/TooltipContext';
import { Separator } from "@/components/ui/separator";
import { BookUser } from 'lucide-react';

// Define Poet type based on schema
type Poet = Database['public']['Tables']['poets']['Row'];

// Hardcoded IDs of the three protected poems
const PROTECTED_POEM_IDS = [
  'fb01f7ab-f6a5-4e00-b268-f50f2db3beb5', // Winter Moose
  '42d2a98d-0f93-4903-86d4-808d420d9016', // moose(a tiny large)
  '316a1b3f-68ee-4272-b886-c1dec4a02777'  // T. S. Eliot - Preludes
];

// Helper to get random subset
const getRandomSubset = <T,>(arr: T[], size: number): T[] => {
  if (size >= arr.length) return [...arr];
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, size);
};

// Helper for localStorage
const usePersistentState = (key: string, defaultValue: boolean): [boolean, (value: boolean) => void] => {
  const [state, setState] = useState<boolean>(() => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key “${key}”:`, error);
      return defaultValue;
    }
  });

  const setPersistentState = (value: boolean) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      setState(value);
    } catch (error) {
      console.error(`Error setting localStorage key “${key}”:`, error);
    }
  };
  return [state, setPersistentState];
};

const Index: React.FC = () => {
  const [poem1Data, setPoem1Data] = useState<PoemWithHighlights | null>(null);
  const [poem2Data, setPoem2Data] = useState<PoemWithHighlights | null>(null);
  const [poet1Info, setPoet1Info] = useState<Poet | null>(null);
  const [poet2Info, setPoet2Info] = useState<Poet | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showHighlights, setShowHighlights] = usePersistentState('homeShowHighlights', false);
  const [activeHighlight1, setActiveHighlight1] = useState<string | null>(null);
  const [activeHighlight2, setActiveHighlight2] = useState<string | null>(null);

  // Fetch poems
  useEffect(() => {
    const fetchPoems = async () => {
      setLoading(true);
      setFetchError(null);
      setPoet1Info(null);
      setPoet2Info(null);
      try {
        const selectedIds = getRandomSubset(PROTECTED_POEM_IDS, 2);
        if (selectedIds.length < 2) throw new Error("Could not select two distinct poems.");
        const [poem1Result, poem2Result] = await Promise.all([
          getPoemById(selectedIds[0]),
          getPoemById(selectedIds[1])
        ]);
        if (!poem1Result || !poem2Result) throw new Error("Failed to fetch one or both poems. Received null.");
        setPoem1Data(poem1Result);
        setPoem2Data(poem2Result);
      } catch (err) {
        console.error("Error fetching poems for homepage:", err);
        setFetchError(err instanceof Error ? err.message : "An unknown error occurred fetching poems.");
        setPoem1Data(null); setPoem2Data(null); setLoading(false);
      }
    };
    fetchPoems();
  }, []);

  // Fetch poets
  useEffect(() => {
    const fetchPoets = async () => {
      if (!poem1Data?.poet_id || !poem2Data?.poet_id) {
        if (fetchError) setLoading(false); // Ensure loading stops if poem fetch failed
        return;
      }
      setLoading(true); // Start loading for poet fetch
      setPoet1Info(null); setPoet2Info(null);
      let poetFetchErrorOccurred = false;
      try {
        const results = await Promise.allSettled([
          getPoetByIdService(poem1Data.poet_id),
          getPoetByIdService(poem2Data.poet_id)
        ]);
        if (results[0].status === 'fulfilled' && results[0].value.data) setPoet1Info(results[0].value.data);
        else { console.error("Poet 1 fetch failed:", results[0].status === 'rejected' ? results[0].reason : results[0].value?.error); poetFetchErrorOccurred = true; }
        if (results[1].status === 'fulfilled' && results[1].value.data) setPoet2Info(results[1].value.data);
        else { console.error("Poet 2 fetch failed:", results[1].status === 'rejected' ? results[1].reason : results[1].value?.error); poetFetchErrorOccurred = true; }
        if (poetFetchErrorOccurred && !fetchError) setFetchError("Failed to fetch poet details.");
      } catch (err) {
        console.error("General error fetching poet details:", err);
        if (!fetchError) setFetchError(err instanceof Error ? err.message : "Unknown error fetching poets.");
      } finally {
        setLoading(false); // Stop loading after poet fetch attempt
      }
    };
    if (poem1Data?.poet_id && poem2Data?.poet_id && !fetchError) fetchPoets();
    else if (!loading && fetchError) setLoading(false); // Ensure loading stops if poems already failed
  }, [poem1Data, poem2Data, fetchError]);

  // Process highlights
  const processHighlights = (
      poemData: PoemWithHighlights | null,
      activeHighlight: string | null
  ): ProcessedHighlightData | null => {
      if (!poemData || !poemData.content || !showHighlights) return null;
      const contentLines = typeof poemData.content === 'string' ? poemData.content.split('\n') : [];
      const highlights = Array.isArray(poemData.highlights) ? poemData.highlights : [];
      if (contentLines.length === 0 || highlights.length === 0) return null;
      try {
          return highlightProcessor.processHighlightsForDisplay(
              contentLines, highlights as PoemHighlight[], activeHighlight || undefined
          );
      } catch (procError) {
          console.error("Error processing highlights:", procError);
          return null;
      }
  };

  const processedData1 = useMemo(() => processHighlights(poem1Data, activeHighlight1), [poem1Data, showHighlights, activeHighlight1]);
  const processedData2 = useMemo(() => processHighlights(poem2Data, activeHighlight2), [poem2Data, showHighlights, activeHighlight2]);

  // Render Poem function - Adjusted for aligned separator
  const renderPoem = (
    poemData: PoemWithHighlights | null,
    poetInfo: Poet | null,
    processedData: ProcessedHighlightData | null,
    activeHighlight: string | null,
    setActiveHighlight: (id: string | null) => void,
    poemIndex: number
  ) => {
    if (!poemData?.content) return null;
    const contentLines = poemData.content.split('\n');
    const highlights = Array.isArray(poemData.highlights) ? poemData.highlights : [];
    let stylePointsArray: string[] = [];
    if (poetInfo?.style_points && Array.isArray(poetInfo.style_points)) {
      stylePointsArray = poetInfo.style_points.filter((item): item is string => typeof item === 'string');
    }
    const legendItems = processedData?.legendItems || [];

    return (
      <Card className="border shadow-sm bg-card flex flex-col h-full">
        <CardContent className="p-6 flex-grow flex flex-col">
          {/* --- Top Section (Fixed Height) --- */}
          <div className="flex-shrink-0 max-w-2xl mx-auto w-full mb-8 text-center">
            <h2 className="font-poetry text-3xl font-semibold mb-3 text-[#403E43]">{poemData.title}</h2>
            <p className="text-sm text-muted-foreground italic">
              <em>Inspired by the style of</em>{' '}
              <span className="font-semibold text-[#403E43]">{poemData.poet_name}</span>
            </p>
          </div>

          {/* --- Poem Content Section (Grows and Scrolls) --- */}
          <div className="flex-grow overflow-y-auto custom-scrollbar"> {/* Removed mb-6 */}
            <HighlightTooltipProvider>
              <div className="space-y-4 font-poetry text-lg leading-relaxed">
                {contentLines.map((line, index) => (
                  <HighlightedLine
                    key={`${poemData.id}-line-${index}`}
                    line={line} lineIndex={index}
                    lineHighlights={processedData?.highlightsByLine?.[index] || []}
                    showHighlights={showHighlights} activeHighlightId={activeHighlight || undefined}
                    onHighlightInteraction={setActiveHighlight}
                    data-testid={`poem${poemIndex}-line-${index}`}
                  />
                ))}
              </div>
            </HighlightTooltipProvider>
          </div>

          {/* --- Aligned Separator --- */}
          {/* Render separator only if there's content below it */}
          {( (showHighlights && legendItems.length > 0) || poetInfo ) && (
             <Separator className="my-4 flex-shrink-0" />
          )}


          {/* --- Footer Section (Fixed Height Content Below Separator) --- */}

          {/* Highlight Legend (Conditional) */}
          {showHighlights && legendItems.length > 0 && (
            <div className="flex-shrink-0 mb-4"> {/* Added mb-4 */}
              <HighlightLegend
                legendItems={legendItems}
                showHighlights={showHighlights}
                activeHighlightId={activeHighlight || undefined}
                onHighlightInteraction={setActiveHighlight}
                data-testid={`poem${poemIndex}-highlight-legend`}
              />
            </div>
          )}

          {/* Style Points Section (Conditional) */}
          {poetInfo && (
            <div className="flex-shrink-0"> {/* Removed mt-auto, using natural flow now */}
              <div className="flex items-start space-x-2">
                <BookUser className="h-5 w-5 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-[#403E43] mb-2">About {poemData.poet_name}'s Style</h3>
                  {stylePointsArray.length > 0 ? (
                    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1">
                      {stylePointsArray.map((point, index) => <li key={index}>{point}</li>)}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground ml-1">No style points available.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-center mb-4 font-poetry">Welcome to the Moose Verse</h1>
      <p className="text-center text-muted-foreground mb-8">Create and read poems inspired by the poetic styles of great poets. Turn on 'Highlights' to read styles notes.</p>
      <div className="flex justify-center items-center space-x-2 mb-8">
        <Switch checked={showHighlights} onCheckedChange={setShowHighlights} id="home-highlight-toggle"/>
        <Label htmlFor="home-highlight-toggle" className="text-sm">Show Highlights</Label>
      </div>
      {loading && <LoadingState>Loading poems and poet details...</LoadingState>}
      {fetchError && !loading && <ErrorState error={new Error(fetchError)} title="Failed to load page data" />}
      {!loading && !fetchError && poem1Data && poem2Data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {renderPoem(poem1Data, poet1Info, processedData1, activeHighlight1, setActiveHighlight1, 1)}
          {renderPoem(poem2Data, poet2Info, processedData2, activeHighlight2, setActiveHighlight2, 2)}
        </div>
      )}
    </div>
  );
};

export default Index;